/**
 * kult-seminar-calendar.js — Seminarkalender als Buchungssystem.
 * Datenquelle: <script type="application/json" data-sem-terms> (Varianten des Seminar-Produkts:
 * id, date "JJJJ-MM-TT", time "HH:MM", end "HH:MM" (leer = unbekannt), dur in Minuten, status, private,
 * seats, open, price in Cent). Uhrzeit-Text überall über timeLabel(t): „15:00–18:00 Uhr · 3 Std.“.
 * Rendert Monatsansicht + synchronisierte Terminliste, Personen-Stepper, Summe.
 * Buchung: POST /cart/add.js → CustomEvent 'kult:cart:add' {cart, title} (Drawer öffnet).
 * Reiter „Termin verschieben“: nach Formular-Post (#kult-sem-move-…) automatisch geöffnet.
 * URL: ?termin=<Varianten-ID> wählt einen Termin vor.
 */
(function () {
  'use strict';
  if (customElements.get('kult-seminar-calendar')) return;

  var MONTHS = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
  var fmtLong = new Intl.DateTimeFormat('de-DE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  var fmtShort = new Intl.DateTimeFormat('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' });

  function money(cents) { return (cents / 100).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' }); }
  function toDate(iso) { var p = iso.split('-'); return new Date(+p[0], +p[1] - 1, +p[2]); }
  function ymd(d) { return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0'); }

  /* Dauer in Minuten als Text: „45 Min.“, „3 Std.“, „2,5 Std.“ (sonst „1 Std. 40 Min.“); ohne Dauer leer. */
  function durLabel(min) {
    min = Math.round(Number(min)) || 0;
    if (min < 1) return '';
    if (min < 60) return min + ' Min.';
    var h = Math.floor(min / 60), r = min % 60;
    if (r === 0) return h + ' Std.';
    if (r === 30) return h + ',5 Std.';
    return h + ' Std. ' + r + ' Min.';
  }
  /* Uhrzeit eines Termins: „15:00–18:00 Uhr · 3 Std.“; fehlt t.end nur „15:00 Uhr“; fehlt t.time leer. */
  function timeLabel(t) {
    if (!t || !t.time) return '';
    if (!t.end) return t.time + ' Uhr';
    var dur = durLabel(t.dur);
    return t.time + '–' + t.end + ' Uhr' + (dur ? ' · ' + dur : '');
  }

  class KultSeminarCalendar extends HTMLElement {
    connectedCallback() {
      var self = this;
      try { this.allTerms = JSON.parse(((this.querySelector('[data-sem-terms]') || {}).textContent || '[]').replace(/\[\s*,/, '[')); } catch (e) { this.allTerms = []; }
      this.allTerms.sort(function (a, b) { return (a.date + a.time).localeCompare(b.date + b.time); });
      this.format = 'a';
      this.terms = this.allTerms.filter(function (t) { return (t.p || 'a') === 'a'; });
      this.max = parseInt(this.dataset.max, 10) || 10;
      this.min = parseInt(this.dataset.min, 10) || 4;
      this.cutoffHours = parseInt(this.dataset.cutoffHours, 10) || 0;
      this.price = parseInt(this.dataset.price, 10) || 0;
      this.qty = 1;
      this.selected = null;
      this.listLimit = parseInt(this.dataset.listLimit, 10) || 0;
      this.listExpanded = false;
      this.today = new Date(); this.today.setHours(0, 0, 0, 0);

      var first = this.terms.filter(function (t) { return t.open; })[0] || this.terms[0];
      var start = first ? toDate(first.date) : this.today;
      this.view = { y: start.getFullYear(), m: start.getMonth() };

      this.addEventListener('click', this._onClick.bind(this));
      this.addEventListener('keydown', this._onKeydown.bind(this));
      this.addEventListener('change', this._onChange.bind(this));

      this.renderList();
      this.renderMonth();
      this.renderPanel();

      var params = new URLSearchParams(window.location.search);
      var pre = params.get('termin');
      if (pre) this.select(parseInt(pre, 10));

      var formId = this.dataset.formId;
      if (formId && (window.location.hash === '#' + formId || params.has('contact_posted'))) {
        this.showTab('move');
        var status = this.querySelector('[data-sem-move-success], [data-sem-move-errors]');
        if (status) { status.focus && status.focus(); status.scrollIntoView({ block: 'center' }); }
      }
      self.classList.add('is-ready');
    }

    /* ---------- Daten ---------- */
    termsOn(date) { return this.terms.filter(function (t) { return t.date === date; }); }
    seatsClass(t) {
      if (!t.open || t.seats <= 0) return 'full';
      return t.seats <= 3 ? 'few' : 'open';
    }
    seatsText(t) {
      if (t.seats <= 0) return 'Ausgebucht';
      if (!t.open) return 'Online-Buchung geschlossen';
      if (t.seats <= 3) return 'Nur noch ' + t.seats + (t.seats === 1 ? ' Platz' : ' Plätze');
      return 'noch ' + t.seats + ' von ' + this.max + ' Plätzen frei';
    }

    /* ---------- Rendering ---------- */
    renderMonth() {
      var self = this, grid = this.querySelector('[data-sem-days]'), label = this.querySelector('[data-sem-month-label]');
      if (!grid) return;
      var y = this.view.y, m = this.view.m;
      if (label) label.textContent = MONTHS[m] + ' ' + y;
      var firstDay = new Date(y, m, 1), offset = (firstDay.getDay() + 6) % 7, daysInMonth = new Date(y, m + 1, 0).getDate();
      var html = '';
      for (var i = 0; i < offset; i++) html += '<span class="kult-sem-cal__day kult-sem-cal__day--muted" aria-hidden="true"></span>';
      for (var d = 1; d <= daysInMonth; d++) {
        var date = new Date(y, m, d), iso = ymd(date), list = this.termsOn(iso), isToday = date.getTime() === this.today.getTime();
        var cls = 'kult-sem-cal__day' + (isToday ? ' kult-sem-cal__day--today' : '');
        if (!list.length) {
          html += '<span class="' + cls + (date < this.today ? ' kult-sem-cal__day--past' : '') + '">' + d + '</span>';
          continue;
        }
        var t = list.filter(function (x) { return x.open; })[0] || list[0];
        var state = this.seatsClass(t);
        var pressed = this.selected && this.selected.date === iso;
        var extra = state === 'full' ? (t.seats <= 0 ? ' kult-sem-cal__day--full' : ' kult-sem-cal__day--closed') : '';
        var aria = fmtLong.format(date) + ', ' + this.seatsText(t);
        html += '<button type="button" class="' + cls + ' kult-sem-cal__day--term' + extra + '" data-sem-term="' + t.id + '" aria-pressed="' + (pressed ? 'true' : 'false') + '" aria-label="' + aria + '"' + (state === 'full' && t.seats <= 0 ? ' aria-disabled="true"' : '') + '>' + d + '<span class="kult-sem-cal__dot kult-sem-cal__dot--' + state + '" aria-hidden="true"></span></button>';
      }
      grid.innerHTML = html;
      var prev = this.querySelector('[data-sem-month="-1"]');
      if (prev) prev.disabled = (y < this.today.getFullYear()) || (y === this.today.getFullYear() && m <= this.today.getMonth());
      var next = this.querySelector('[data-sem-month="1"]');
      if (next) {
        var last = this.terms.length ? toDate(this.terms[this.terms.length - 1].date) : this.today;
        next.disabled = (y > last.getFullYear()) || (y === last.getFullYear() && m >= last.getMonth());
      }
    }

    renderList() {
      var self = this, shown = 0, hiddenCount = 0;
      this.querySelectorAll('[data-sem-list] [data-sem-term]').forEach(function (btn) {
        var li = btn.closest('li');
        if (li && (li.dataset.semProduct || 'a') !== self.format) { li.classList.add('kult-sem-cal__item-wrap--hidden'); return; }
        if (li) {
          var hide = self.listLimit > 0 && !self.listExpanded && shown >= self.listLimit;
          if (self.selected && self.selected.id === parseInt(btn.dataset.semTerm, 10)) hide = false;
          li.classList.toggle('kult-sem-cal__item-wrap--hidden', hide);
          if (hide) hiddenCount++; else shown++;
        }
        var t = self.terms.filter(function (x) { return x.id === parseInt(btn.dataset.semTerm, 10); })[0];
        if (!t) { btn.closest('li').classList.add('kult-sem-cal__item-wrap--hidden'); return; }
        var seats = btn.querySelector('[data-sem-item-seats]');
        var state = self.seatsClass(t);
        if (seats) { seats.textContent = self.seatsText(t); seats.classList.toggle('kult-sem-cal__item-seats--few', state === 'few'); }
        btn.classList.toggle('kult-sem-cal__item--full', state === 'full');
        if (state === 'full') btn.setAttribute('aria-disabled', 'true');
        var dateEl = btn.querySelector('.kult-sem-cal__item-date');
        if (dateEl) {
          dateEl.textContent = fmtShort.format(toDate(t.date));
          if (t.time) {
            var tm = document.createElement('span');
            tm.className = 'kult-sem-cal__time';
            tm.textContent = timeLabel(t);
            dateEl.appendChild(document.createTextNode(', '));
            dateEl.appendChild(tm);
          }
        }
        btn.setAttribute('aria-pressed', self.selected && self.selected.id === t.id ? 'true' : 'false');
      });
      var more = this.querySelector('[data-sem-more]');
      if (more) {
        more.hidden = !(this.listLimit > 0 && (hiddenCount > 0 || this.listExpanded) && this.terms.length > this.listLimit);
        more.setAttribute('aria-expanded', this.listExpanded ? 'true' : 'false');
        if (!more.dataset.label) more.dataset.label = more.textContent;
        more.textContent = this.listExpanded ? 'Weniger Termine' : more.dataset.label + (hiddenCount ? ' (' + hiddenCount + ')' : '');
      }
    }

    renderPanel() {
      var t = this.selected, hint = this.querySelector('[data-sem-select]'), sum = this.querySelector('[data-sem-summary]');
      if (!sum) return;
      if (!t) { hint.hidden = false; sum.hidden = true; return; }
      hint.hidden = true; sum.hidden = false;
      var date = toDate(t.date);
      this.querySelector('[data-sem-date]').textContent = fmtLong.format(date);
      var meta = this.dataset.meta || '', tl = timeLabel(t);
      this.querySelector('[data-sem-time]').textContent = tl + (tl && meta ? ', ' : '') + meta;
      var seats = this.querySelector('[data-sem-seats]'), state = this.seatsClass(t);
      seats.textContent = this.seatsText(t);
      seats.className = 'kult-sem-cal__seats' + (state === 'few' ? ' kult-sem-cal__seats--few' : '') + (state === 'full' ? ' kult-sem-cal__seats--none' : '');
      var bookable = t.open && t.seats > 0, limit = Math.min(t.seats, this.max);
      if (this.qty > limit) this.qty = Math.max(1, limit);
      this.querySelector('[data-sem-qty]').textContent = String(this.qty);
      this.querySelector('[data-sem-step="-1"]').disabled = !bookable || this.qty <= 1;
      this.querySelector('[data-sem-step="1"]').disabled = !bookable || this.qty >= limit;
      this.querySelector('[data-sem-qty-row]').hidden = !bookable;
      this.querySelector('[data-sem-sum-label]').textContent = this.qty + ' × ' + money(t.price || this.price);
      this.querySelector('[data-sem-sum]').textContent = money((t.price || this.price) * this.qty);
      var btn = this.querySelector('[data-sem-book]');
      btn.disabled = !bookable;
      btn.textContent = bookable ? (btn.dataset.label || (btn.dataset.label = btn.textContent)) : (t.seats <= 0 ? (this.dataset.labelFull || 'Ausgebucht') : (this.dataset.labelClosed || 'Bitte telefonisch buchen'));
      this.message('');
    }

    message(text, ok) {
      var el = this.querySelector('[data-sem-msg]');
      if (!el) return;
      el.textContent = text || '';
      el.hidden = !text;
      el.classList.toggle('kult-sem-cal__msg--ok', !!ok);
    }

    /* ---------- Interaktion ---------- */
    select(id) {
      var t = this.terms.filter(function (x) { return x.id === id; })[0];
      if (!t) return;
      this.selected = t;
      var d = toDate(t.date);
      if (d.getFullYear() !== this.view.y || d.getMonth() !== this.view.m) this.view = { y: d.getFullYear(), m: d.getMonth() };
      this.renderMonth();
      this.renderList();
      this.renderPanel();
    }

    setFormat(f) {
      this.format = f;
      var self = this;
      var onsite = f === 'c';
      var booking = this.querySelector('[data-sem-booking]'), listWrap = this.querySelector('[data-sem-list-wrap]'), rules = this.querySelector('[data-sem-rules]'), on = this.querySelector('[data-sem-onsite]');
      if (booking) booking.hidden = onsite;
      if (listWrap) listWrap.hidden = onsite;
      if (rules) rules.hidden = onsite;
      if (on) on.hidden = !onsite;
      if (onsite) return;
      this.terms = this.allTerms.filter(function (t) { return (t.p || 'a') === f; });
      this.selected = null; this.qty = 1; this.listExpanded = false;
      var first = this.terms.filter(function (t) { return t.open; })[0] || this.terms[0];
      if (first) { var d = toDate(first.date); this.view = { y: d.getFullYear(), m: d.getMonth() }; }
      this.renderList(); this.renderMonth(); this.renderPanel();
    }

    _onChange(e) {
      var r = e.target.closest('[data-sem-format]');
      if (r && r.checked) this.setFormat(r.value);
      var g = e.target.closest('[data-sem-gift-toggle]');
      if (g) {
        var fields = this.querySelector('[data-sem-gift-fields]');
        if (fields) fields.hidden = !g.checked;
        g.setAttribute('aria-expanded', g.checked ? 'true' : 'false');
        if (g.checked) { var f = this.querySelector('[data-sem-gift-fuer]'); if (f) { f.scrollIntoView({ block: 'center', behavior: 'smooth' }); f.focus({ preventScroll: true }); } }
      }
    }

    showTab(name) {
      this.querySelectorAll('[data-sem-tab]').forEach(function (tab) {
        var on = tab.dataset.semTab === name;
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
        tab.tabIndex = on ? 0 : -1;
      });
      this.querySelectorAll('[data-sem-panel]').forEach(function (p) { p.hidden = p.dataset.semPanel !== name; });
    }

    _onClick(e) {
      var tab = e.target.closest('[data-sem-tab]');
      if (tab) { this.showTab(tab.dataset.semTab); tab.focus(); return; }
      if (e.target.closest('[data-sem-more]')) { this.listExpanded = !this.listExpanded; this.renderList(); return; }
      var nav = e.target.closest('[data-sem-month]');
      if (nav) {
        var m = this.view.m + parseInt(nav.dataset.semMonth, 10), y = this.view.y;
        if (m < 0) { m = 11; y--; } else if (m > 11) { m = 0; y++; }
        this.view = { y: y, m: m }; this.renderMonth(); return;
      }
      var term = e.target.closest('[data-sem-term]');
      if (term) { this.select(parseInt(term.dataset.semTerm, 10)); if (term.closest('[data-sem-list]')) { var b = this.querySelector('[data-sem-summary]'); if (b && window.innerWidth < 960) b.scrollIntoView({ block: 'nearest', behavior: 'smooth' }); } return; }
      var step = e.target.closest('[data-sem-step]');
      if (step && this.selected) { this.qty = Math.max(1, this.qty + parseInt(step.dataset.semStep, 10)); this.renderPanel(); return; }
      if (e.target.closest('[data-sem-book]')) this.book();
    }

    _onKeydown(e) {
      var tab = e.target.closest('[data-sem-tab]');
      if (!tab || (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight')) return;
      e.preventDefault();
      var tabs = Array.prototype.slice.call(this.querySelectorAll('[data-sem-tab]'));
      var i = tabs.indexOf(tab), next = tabs[(i + (e.key === 'ArrowRight' ? 1 : tabs.length - 1)) % tabs.length];
      this.showTab(next.dataset.semTab); next.focus();
    }

    /* „Als Geschenk buchen“ (Later-Liste 22.10. Nr. 7): Felder ein-/ausblenden; Eigenschaften für die Bestellung */
    giftProps() {
      var box = this.querySelector('[data-sem-gift]');
      var on = box && box.querySelector('[data-sem-gift-toggle]').checked;
      if (!on) return {};
      var val = function (sel) { var el = box.querySelector(sel); return el ? el.value.replace(/\s+/g, ' ').trim() : ''; };
      var fuer = val('[data-sem-gift-fuer]');
      if (!fuer) return null;
      var p = { 'Geschenk': 'Ja', 'Für': fuer.slice(0, 60), '_kult_geschenk': '1' };
      var von = val('[data-sem-gift-von]'), gruss = val('[data-sem-gift-gruss]');
      if (von) p['Von'] = von.slice(0, 60);
      if (gruss) p['Grußtext'] = gruss.slice(0, 160);
      return p;
    }

    book() {
      var self = this, t = this.selected, btn = this.querySelector('[data-sem-book]');
      if (!t || !t.open || btn.disabled) return;
      var gift = this.giftProps();
      if (gift === null) {
        this.message('Bitte trag ein, wen du beschenkst (Feld „Für“).');
        var f = this.querySelector('[data-sem-gift-fuer]'); if (f) { f.scrollIntoView({ block: 'center', behavior: 'smooth' }); f.focus({ preventScroll: true }); }
        return;
      }
      btn.disabled = true; btn.setAttribute('aria-busy', 'true');
      var title = (this.format === 'b' && this.dataset.titleB ? this.dataset.titleB : this.dataset.title) + ' am ' + fmtShort.format(toDate(t.date));
      var props = this.dataset.venue ? { 'Ort': this.dataset.venue } : {};
      Object.keys(gift).forEach(function (k) { props[k] = gift[k]; });
      fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify({ items: [{ id: t.id, quantity: this.qty, properties: props }] }) })
        .then(function (r) { return r.json().then(function (d) { return { ok: r.ok, data: d }; }); })
        .then(function (res) {
          if (!res.ok) {
            var desc = (res.data && (res.data.description || res.data.message)) || 'Der Termin konnte nicht in den Warenkorb gelegt werden.';
            if (/verfügbar|available|stock|Bestand/i.test(desc)) desc = 'So viele Plätze sind für diesen Termin nicht mehr frei. Bitte Personenzahl anpassen oder einen anderen Termin wählen.';
            self.message(desc); return;
          }
          return fetch('/cart.js', { headers: { 'Accept': 'application/json' } }).then(function (r) { return r.json(); }).then(function (cart) {
            var n = self.qty + (self.qty === 1 ? ' Platz' : ' Plätze');
            self.message((self.dataset.success || '{n} im Warenkorb.').replace('{n}', n), true);
            document.dispatchEvent(new CustomEvent('kult:cart:add', { detail: { cart: cart, title: title } }));
          });
        })
        .catch(function () { self.message('Verbindung fehlgeschlagen. Bitte noch einmal versuchen.'); })
        .then(function () { btn.disabled = false; btn.removeAttribute('aria-busy'); });
    }
  }

  customElements.define('kult-seminar-calendar', KultSeminarCalendar);
})();
